/* iter-53n++ pass 4 — "New from template" picker.
 *
 * Renders a two-column grid: a "Start blank" tile followed by every
 * template the current user owns. Each template card shows:
 *   - Title
 *   - Genre badge + chapter count + character count
 *   - Last-used relative date
 *   - Mood-tag chips (story_bible.mood_tags or story_bible.themes)
 *
 * Two consumption points (per user choice 1c):
 *   1. Workspace empty-state — shown when no project is loaded.
 *   2. Sidebar tab (activity bar → "Templates") — always available.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, PlusSquare, Wand2, BookOpen, Users, Sparkles, Feather, MoreVertical, Eye, Pencil, Trash2, Store, CircleCheck, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { templatesApi, marketplaceApi, chaptersApi, charactersApi } from '@/lib/api';
import { usePW } from '@/lib/store';
import TemplatePreviewDialog from './TemplatePreviewDialog';
import CommonTraitsDialog from './CommonTraitsDialog';
import TemplateVariablesDialog, { interp } from './TemplateVariablesDialog';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/** Format an ISO timestamp as a relative "3 days ago" style string. */
function timeAgo(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now  = Date.now();
    const diff = Math.max(0, now - then);
    const min = Math.floor(diff / 60_000);
    if (min < 1)     return 'just now';
    if (min < 60)    return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24)     return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 30)   return `${days}d ago`;
    const mo = Math.floor(days / 30);
    if (mo < 12)     return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
  } catch { return ''; }
}

export function TemplatesPicker({ onNewBlank, embedded = false, onClose }) {
  const { refreshProjects, loadProject } = usePW();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [spawning, setSpawning] = useState(null); // template id currently spinning up
  // iter-53n++ pass 5 — Rename inline + Delete + Preview.
  const [renamingId, setRenamingId]   = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [previewId, setPreviewId]     = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  // iter55f96 — Common-traits dialog state. When a user starts a
  // project from a template we intercept the spawn, fetch the
  // template's character skeletons via /api/templates/{tid}/outline,
  // and open the dialog so they can pick common traits. On confirm,
  // we spawn the project THEN PUT each character's traits.
  const [traitsDialog, setTraitsDialog] = useState(null); // { tpl, characters }
  const [traitsBusy, setTraitsBusy]     = useState(false);
  // iter55f98 — Book-template .docx drop-zone state. Once a file is
  // parsed we get back a framework; we then reuse the same
  // CommonTraitsDialog and, on confirm, PATCH the framework's traits
  // and materialize it into a The Loom project.
  const [docxImporting, setDocxImporting] = useState(false);
  const [docxDialog, setDocxDialog]       = useState(null); // { framework, characters }
  const [dragging, setDragging]           = useState(false);
  const fileInputRef                       = useRef(null);
  // iter55g50 — Marketplace-prompt state. When a freshly spawned
  // project has any `{{token}}` in its chapters/characters we open a
  // TemplateVariablesDialog with an inferred text-only schema, collect
  // the values, then PATCH each affected row with interpolated content.
  const [varsCtx, setVarsCtx] = useState(null); // { pseudoTpl, projId, projName }
  const [varsBusy, setVarsBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await templatesApi.list();
      setTemplates(r.templates || []);
    } catch (e) {
      // 401 in demo mode — hide the picker rather than nag.
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || 'Could not load templates.');
      }
      setTemplates([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const spawnFromTemplate = async (tpl) => {
    setSpawning(tpl.id);
    try {
      // iter55f96 — Peek at the template's character skeletons first.
      // If there are any, open the Common-Traits dialog before we
      // actually spawn the project so the writer can personalise.
      let charSkeletons = [];
      try {
        const outline = await templatesApi.outline(tpl.id);
        charSkeletons = (outline?.characters || []).map((c) => ({
          id:     c.id,
          name:   c.name || 'Unnamed',
          role:   c.role || 'Supporting',
          traits: Array.isArray(c.traits) ? c.traits : [],
        }));
      } catch { /* No outline endpoint / permission — fall through to no-personalisation. */ }
      if (charSkeletons.length > 0) {
        setTraitsDialog({ tpl, characters: charSkeletons });
        setSpawning(null);
        return;
      }
      // No characters → boot the template directly.
      await _bootTemplate(tpl, {}, []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not spin up template.');
      setSpawning(null);
    }
  };

  // Shared boot path — spawns the project, applies per-character traits,
  // and hands off to the workspace. Called both from the no-characters
  // fast-path above and from the Common-Traits dialog's onConfirm.
  //
  // iter55f96 — Cloned characters get fresh IDs, so we can't PUT
  // traits against the TEMPLATE's IDs. We keep a name-keyed map from
  // the picker dialog and match to the cloned characters by name +
  // role once the fresh project's roster is loaded.
  const _bootTemplate = async (tpl, traitsByTemplateCid, templateCharacters = []) => {
    const proj = await templatesApi.fromTemplate(tpl.id);
    toast.success(`Started "${proj.name}" from template.`);
    // Build a NAME → traits[] map from the template-cid → traits[]
    // map + the template character list.
    const traitsByName = {};
    for (const [tcid, traits] of Object.entries(traitsByTemplateCid || {})) {
      if (!Array.isArray(traits) || traits.length === 0) continue;
      const src = templateCharacters.find((c) => c.id === tcid);
      if (src?.name) traitsByName[src.name] = traits;
    }
    if (Object.keys(traitsByName).length > 0) {
      try {
        const cloned = await axios.get(`${API}/projects/${proj.id}/characters`,
          { withCredentials: true, timeout: 15000 });
        const list = cloned.data?.characters || cloned.data || [];
        for (const ch of list) {
          const traits = traitsByName[ch.name];
          if (!traits) continue;
          try {
            await axios.put(`${API}/characters/${ch.id}`, { traits },
              { withCredentials: true });
          } catch { /* keep going; the character keeps template defaults */ }
        }
      } catch { /* couldn't fetch clone roster — carry on without personalisation */ }
    }
    if (refreshProjects) await refreshProjects();
    if (proj?.id && loadProject) await loadProject(proj.id, { silent: false });

    // iter55g50 — Marketplace prompt. Scan the spawned project's
    // chapters + characters for `{{tokens}}`. If any exist, open the
    // TemplateVariablesDialog with an inferred schema and defer
    // closing the picker until the user submits.
    try {
      const chs = await chaptersApi.list(proj.id).catch(() => []);
      const chars = await charactersApi.list(proj.id).catch(() => ({ characters: [] }));
      const charList = chars?.characters || chars || [];
      const tokenRe = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
      const tokens = new Set();
      const scan = (s) => {
        if (typeof s !== 'string') return;
        let m;
        while ((m = tokenRe.exec(s)) !== null) tokens.add(m[1].toLowerCase());
      };
      chs.forEach((c) => { scan(c.title); scan(c.content); });
      charList.forEach((c) => { scan(c.name); scan(c.description); });
      if (tokens.size > 0) {
        const humanize = (t) => t.replace(/_/g, ' ')
                                   .replace(/\b\w/g, (x) => x.toUpperCase());
        const pseudoTpl = {
          name: proj.name || 'this template',
          variables: [...tokens].map((k) => ({
            key: k, label: humanize(k), type: 'text',
            placeholder: `Value for {{${k}}}`,
          })),
          chapters: [], characters: [], // interp targets provided below
        };
        setVarsCtx({ pseudoTpl, projId: proj.id, chapters: chs, characters: charList });
        return; // don't close the picker yet — the dialog will close it.
      }
    } catch (_e) { /* scan failed — carry on with the un-personalised project */ }

    if (onClose) onClose();
  };

  // iter55g50 — Called by TemplateVariablesDialog once the user submits
  // their token values. Patches each chapter + character in place.
  const _applyMarketplaceVars = async (_materialised, values) => {
    if (!varsCtx) return;
    setVarsBusy(true);
    try {
      for (const c of (varsCtx.chapters || [])) {
        const patch = {};
        if (c.title   && /\{\{/.test(c.title))   patch.title   = interp(c.title,   values);
        if (c.content && /\{\{/.test(c.content)) patch.content = interp(c.content, values);
        if (Object.keys(patch).length > 0) {
          try { await chaptersApi.update(c.id, patch); } catch (_e) { /* keep going */ }
        }
      }
      for (const ch of (varsCtx.characters || [])) {
        const patch = {};
        if (ch.name        && /\{\{/.test(ch.name))        patch.name        = interp(ch.name,        values);
        if (ch.description && /\{\{/.test(ch.description)) patch.description = interp(ch.description, values);
        if (Object.keys(patch).length > 0) {
          try { await charactersApi.update(ch.id, patch); } catch (_e) { /* keep going */ }
        }
      }
      if (loadProject) await loadProject(varsCtx.projId, { silent: true });
      toast.success('Template personalised.');
      setVarsCtx(null);
      if (onClose) onClose();
    } finally { setVarsBusy(false); }
  };

  const confirmTraitsDialog = async (traitsByCharacter) => {
    if (!traitsDialog) return;
    setTraitsBusy(true);
    try {
      await _bootTemplate(traitsDialog.tpl, traitsByCharacter, traitsDialog.characters);
      setTraitsDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not spin up template.');
    } finally { setTraitsBusy(false); }
  };

  // iter-53n++ pass 5 — Inline rename. Commit on Enter / blur; Esc cancels.
  const startRename = (tpl) => { setRenamingId(tpl.id); setRenameDraft(tpl.name || ''); };
  const commitRename = async (tpl) => {
    const next = (renameDraft || '').trim();
    setRenamingId(null);
    if (!next || next === tpl.name) return;
    try {
      await templatesApi.rename(tpl.id, next);
      setTemplates((prev) => prev.map((t) => (t.id === tpl.id ? { ...t, name: next } : t)));
      toast.success('Template renamed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed.');
    }
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(''); };

  // iter-53n++ pass 5 — Delete with confirm (single-click double-tap: first
  // click primes, second click within 3s confirms). Keeps the picker tidy
  // without a full modal.
  const askDelete = async (tpl) => {
    if (deletingId !== tpl.id) {
      setDeletingId(tpl.id);
      toast.warning(`Click "Delete" again within 3s to remove "${tpl.name}".`);
      setTimeout(() => setDeletingId((cur) => (cur === tpl.id ? null : cur)), 3000);
      return;
    }
    setDeletingId(null);
    try {
      await templatesApi.remove(tpl.id);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success('Template deleted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  // iter-53n++ pass 6 — Publish / unpublish to the community marketplace.
  const publishToMarketplace = async (tpl) => {
    try {
      const r = await marketplaceApi.publish(tpl.id);
      setTemplates((prev) => prev.map((t) => (t.id === tpl.id ? {
        ...t,
        published_to_marketplace: true,
        marketplace_tier: r.tier,
        marketplace_price_cents: r.price_cents,
      } : t)));
      toast.success(`Published "${tpl.name}" — ${r.tier} tier ($${(r.price_cents / 100).toFixed(2)}).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed.');
    }
  };
  const unpublishFromMarketplace = async (tpl) => {
    try {
      await marketplaceApi.unpublish(tpl.id);
      setTemplates((prev) => prev.map((t) => (t.id === tpl.id ? {
        ...t, published_to_marketplace: false,
      } : t)));
      toast.success('Removed from marketplace.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unpublish failed.');
    }
  };

  /* iter55f98 — Book-template .docx flow.
   *
   *   handleDocxFile → POSTs the file to /templates/book-template/import
   *                     which parses + creates a Novel Framework.
   *                     Opens the CommonTraitsDialog with the framework's
   *                     character skeletons.
   *   confirmDocxDialog(traitsByFrameworkCid) → PATCHes the framework's
   *                     `characters[].traits`, POSTs /framework/{fid}/import
   *                     which materializes a The Loom project, then
   *                     loads it into the workspace.
   */
  const handleDocxFile = async (file) => {
    if (!file) return;
    if (!/\.docx$/i.test(file.name)) {
      toast.error('Please pick a .docx book-template file.');
      return;
    }
    setDocxImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const r = await axios.post(`${API}/templates/book-template/import`, fd, {
        withCredentials: true, timeout: 30000,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fw = r.data.framework;
      const chars = (fw?.characters || []).map((c) => ({
        id:     c.id,
        name:   c.name || 'Unnamed',
        role:   c.role || 'Supporting',
        traits: Array.isArray(c.traits) ? c.traits : [],
      }));
      setDocxDialog({ framework: fw, characters: chars });
      if ((r.data.parse_warnings || []).length > 0) {
        toast.warning(`Template imported with ${r.data.parse_warnings.length} parse note${r.data.parse_warnings.length === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read that book template.');
    } finally { setDocxImporting(false); }
  };

  const confirmDocxDialog = async (traitsByFrameworkCid) => {
    if (!docxDialog?.framework) return;
    setTraitsBusy(true);
    try {
      const fw = docxDialog.framework;
      // Patch the framework's character traits before materializing so
      // the trait picks flow into the seeded characters via the
      // /framework/{fid}/import path (which mirrors characters[].traits).
      const patchedChars = (fw.characters || []).map((c) => {
        const picked = traitsByFrameworkCid?.[c.id];
        return Array.isArray(picked) && picked.length > 0
          ? { ...c, traits: picked }
          : c;
      });
      await axios.patch(`${API}/framework/${fw.id}`, { characters: patchedChars },
        { withCredentials: true, timeout: 15000 });
      const r = await axios.post(`${API}/framework/${fw.id}/import`, {},
        { withCredentials: true, timeout: 30000 });
      const newPid = r.data?.project_id;
      if (!newPid) throw new Error('No project id returned');
      toast.success(`Started "${fw.name}" from your book template.`);
      if (refreshProjects) await refreshProjects();
      if (loadProject) await loadProject(newPid, { silent: false });
      setDocxDialog(null);
      if (onClose) onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not materialize the template.');
    } finally { setTraitsBusy(false); }
  };

  const onDropDocx = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleDocxFile(file);
  };

  return (
    <div className={embedded ? 'p-4' : 'mx-auto max-w-5xl p-6 lg:p-10'} data-testid="templates-picker">
      <header className="mb-5 flex items-baseline justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[16px] font-semibold">
            <Wand2 className="h-4 w-4 text-primary" /> Start a new novel
          </h2>
          <p className="mt-1 text-[12px] text-muted-foreground">
            Begin from scratch, or spin up a fresh manuscript that inherits the outline, characters, and story bible from one of your templates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.location.assign('/marketplace')}
            data-testid="templates-open-marketplace"
            className="gap-1.5 text-[11.5px]"
          >
            <Store className="h-3.5 w-3.5" /> Browse marketplace
          </Button>
          {onClose && (
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="templates-picker-close">Close</Button>
          )}
        </div>
      </header>

      {/* iter55f98 — Book-template .docx drop-zone. Sits at the top of
          the list so writers who already have a saved template can
          upload it without hunting through the picker. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDropDocx}
        data-testid="templates-import-docx-dropzone"
        className={`mb-4 flex cursor-pointer items-center gap-3 rounded-md border-2 border-dashed p-4 text-left transition-all ${
          dragging
            ? 'border-primary bg-primary/10'
            : 'border-panel-border hover:border-primary/60 hover:bg-primary/5'
        }`}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          {docxImporting
            ? <Loader2 className="h-5 w-5 animate-spin" />
            : <FileUp className="h-5 w-5" />}
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-semibold" data-testid="templates-import-docx-title">
            Import book template (.docx)
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Drop a saved <b>book-template.docx</b> here — or click to browse. We&apos;ll open the trait picker and boot your new project.
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleDocxFile(f);
            e.target.value = '';
          }}
          data-testid="templates-import-docx-input"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {/* "Start blank" tile — always first. */}
        <button
          onClick={() => { onNewBlank?.(); if (onClose) onClose(); }}
          data-testid="template-start-blank"
          className="group flex h-full min-h-[168px] flex-col items-start justify-between rounded-md border-2 border-dashed border-primary/50 bg-primary/5 p-4 text-left transition-all hover:border-primary hover:bg-primary/10"
        >
          <PlusSquare className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[14px] font-semibold text-primary">Start blank</div>
            <div className="mt-1 text-[11.5px] text-muted-foreground">
              Open the New Project dialog and configure everything from scratch.
            </div>
          </div>
          <div className="text-[10.5px] uppercase tracking-wider text-primary/70 group-hover:text-primary">
            Fresh start →
          </div>
        </button>

        {/* Loading state */}
        {loading && (
          <div className="col-span-full flex items-center justify-center gap-2 rounded-md border border-dashed border-panel-border p-8 text-[12px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your templates…
          </div>
        )}

        {/* Template tiles */}
        {!loading && templates.length === 0 && (
          <div className="col-span-full rounded-md border border-dashed border-panel-border p-6 text-center text-[12px] text-muted-foreground" data-testid="templates-picker-empty">
            <Sparkles className="mx-auto h-5 w-5 text-primary/60" />
            <p className="mt-2">You haven&apos;t saved any templates yet.</p>
            <p className="mt-1 text-[11px]">
              Open any project → <b>File</b> → <b>Save As</b> → tick <b>Save as template</b> to stash its skeleton here.
            </p>
          </div>
        )}

        {!loading && templates.map((t) => (
          <div
            key={t.id}
            className="group flex h-full min-h-[168px] flex-col rounded-md border border-panel-border bg-card p-4 text-left transition-all hover:border-primary/60 hover:shadow-panel"
            data-testid={`template-card-${t.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                {renamingId === t.id ? (
                  <Input
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={() => commitRename(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(t);
                      else if (e.key === 'Escape') cancelRename();
                    }}
                    data-testid={`template-rename-input-${t.id}`}
                    className="h-7 text-[13.5px] font-semibold"
                  />
                ) : (
                  <div className="truncate text-[13.5px] font-semibold" title={t.name}>{t.name}</div>
                )}
                <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10.5px] text-muted-foreground">
                  {t.genre && <Badge variant="outline" className="h-4 px-1.5 font-mono-stat text-[9.5px] capitalize">{t.genre}</Badge>}
                  {t.published_to_marketplace && (
                    <Badge className="h-4 gap-0.5 px-1.5 font-mono-stat text-[9.5px]" data-testid={`template-published-badge-${t.id}`}>
                      <CircleCheck className="h-2.5 w-2.5" /> ${((t.marketplace_price_cents || 0) / 100).toFixed(2)}
                    </Badge>
                  )}
                  {t.last_used_at && <span>· updated {timeAgo(t.last_used_at)}</span>}
                </div>
                {/* iter-romance28 — Archetype-variants preview.
                    Templates seeded with a `genre_variants` library
                    (Romance25, Mystery25, Fantasy25, DR25 v2) show a
                    tiny chip row listing the 5 pickable variants so
                    writers see the flavor at a glance. */}
                {t.genre_variants && typeof t.genre_variants === 'object' && (
                  <div className="mt-1.5 flex flex-wrap gap-1"
                       data-testid={`template-variants-${t.id}`}>
                    {Object.entries(t.genre_variants).slice(0, 5).map(([key, v]) => (
                      <span key={key}
                            className="inline-flex items-center rounded-sm border border-oxblood/40 bg-oxblood/[0.06] px-1.5 py-[1px] text-[9.5px] uppercase tracking-wider text-oxblood">
                        {v.label || key}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {/* iter-53n++ pass 5 — Actions dropdown (Preview / Rename / Delete). */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="rounded-sm p-1 text-muted-foreground opacity-60 transition-opacity hover:bg-secondary hover:text-foreground group-hover:opacity-100"
                    data-testid={`template-actions-${t.id}`}
                    aria-label="Template actions"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px] text-[12px]">
                  <DropdownMenuItem onClick={() => setPreviewId(t.id)} data-testid={`template-preview-${t.id}`}>
                    <Eye className="mr-2 h-3.5 w-3.5" /> Preview outline
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => startRename(t)} data-testid={`template-rename-${t.id}`}>
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Rename…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {t.published_to_marketplace ? (
                    <DropdownMenuItem onClick={() => unpublishFromMarketplace(t)} data-testid={`template-unpublish-${t.id}`}>
                      <Store className="mr-2 h-3.5 w-3.5" /> Remove from marketplace
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => publishToMarketplace(t)} data-testid={`template-publish-${t.id}`}>
                      <Store className="mr-2 h-3.5 w-3.5" /> Publish to marketplace…
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => askDelete(t)}
                    data-testid={`template-delete-${t.id}`}
                    className={deletingId === t.id ? 'text-rose-500 focus:text-rose-500' : 'text-rose-500/80 focus:text-rose-500'}
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    {deletingId === t.id ? 'Click again to confirm' : 'Delete template'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Counts */}
            <div className="mt-2 flex gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><BookOpen className="h-3 w-3 text-primary/70" /> {t.chapter_count} chapters</span>
              <span className="flex items-center gap-1"><Users className="h-3 w-3 text-primary/70" /> {t.character_count} characters</span>
              <span className="flex items-center gap-1"><Feather className="h-3 w-3 text-primary/70" /> {t.words_per_chapter.toLocaleString()} wpc</span>
            </div>

            {/* Mood-tag chip strip (live thumbnail). */}
            {t.mood_tags?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1" data-testid={`template-mood-tags-${t.id}`}>
                {t.mood_tags.slice(0, 5).map((tag, i) => (
                  <span
                    key={`${t.id}-mt-${i}`}
                    className="rounded-full border border-primary/25 bg-primary/8 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary/85"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-auto pt-3">
              <Button
                size="sm"
                onClick={() => spawnFromTemplate(t)}
                disabled={spawning === t.id}
                data-testid={`template-spawn-${t.id}`}
                className="w-full gap-1.5 text-[11.5px]"
              >
                {spawning === t.id
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Creating…</>
                  : <><Wand2 className="h-3.5 w-3.5" /> New from this template</>}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* iter-53n++ pass 5 — Preview outline dialog. Spawn button inside
       *  it delegates back to `spawnFromTemplate` so the user can go
       *  Preview → New from Template in one flow. */}
      <TemplatePreviewDialog
        open={!!previewId}
        onOpenChange={(v) => { if (!v) setPreviewId(null); }}
        templateId={previewId}
        onSpawn={() => {
          const t = templates.find((x) => x.id === previewId);
          if (t) { setPreviewId(null); spawnFromTemplate(t); }
        }}
      />
      {/* iter55f96 — Common-traits dialog before booting the template. */}
      <CommonTraitsDialog
        open={!!traitsDialog}
        onOpenChange={(v) => { if (!v && !traitsBusy) setTraitsDialog(null); }}
        characters={traitsDialog?.characters || []}
        templateName={traitsDialog?.tpl?.name}
        busy={traitsBusy}
        onConfirm={confirmTraitsDialog}
      />
      {/* iter55f98 — Common-traits dialog for the .docx book-template flow. */}
      <CommonTraitsDialog
        open={!!docxDialog}
        onOpenChange={(v) => { if (!v && !traitsBusy) setDocxDialog(null); }}
        characters={docxDialog?.characters || []}
        templateName={docxDialog?.framework?.name}
        busy={traitsBusy}
        onConfirm={confirmDocxDialog}
      />
    </div>
  );
}
